import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  addToast,
  Button,
  Checkbox,
  CheckboxGroup,
  DateField,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Select,
  SelectItem,
  Switch,
  Textarea,
  TimeField,
} from '@structural-climbing/ui'
import { COLOR_SWATCH } from './ClassCard'
import { useCalendarApi } from './useCalendarApi'
import {
  CLASS_COLORS,
  classColor,
  trainerLabel,
  type ClassColor,
  type ClassSession,
  type PersonCard,
} from './types'
import { isoWeekday, timeOf } from './weekRange'

type AgePreset = 'all' | 'kids' | 'adults' | 'custom'
type Scope = 'this' | 'upcoming'

/** The presets fill the ages in; both stay editable. */
const AGE_PRESETS: Record<Exclude<AgePreset, 'custom'>, { min: string; max: string }> = {
  all: { min: '', max: '' },
  kids: { min: '5', max: '17' },
  adults: { min: '18', max: '' },
}

function presetFor(min: string, max: string): AgePreset {
  for (const [key, value] of Object.entries(AGE_PRESETS))
    if (value.min === min && value.max === max) return key as AgePreset
  return 'custom'
}

interface Draft {
  title: string
  description: string
  trainerId: string
  capacity: string
  /** Which age option is picked. Kept apart from the ages: Custom stays picked even while its boxes are empty. */
  agePreset: AgePreset
  minAge: string
  maxAge: string
  color: ClassColor
  repeats: boolean
  weekdays: string[]
  date: string
  startsOn: string
  endsOn: string
  startTime: string
  endTime: string
}

function draftFor(session: ClassSession | null, day: string): Draft {
  if (!session)
    return {
      title: '',
      description: '',
      trainerId: '',
      capacity: '8',
      agePreset: 'all',
      minAge: '',
      maxAge: '',
      color: 'blue',
      repeats: true,
      weekdays: [String(isoWeekday(day))],
      date: day,
      startsOn: day,
      endsOn: '',
      startTime: '17:00',
      endTime: '18:30',
    }
  const sessionDay = session.starts_at.slice(0, 10)
  return {
    title: session.title,
    description: session.description ?? '',
    trainerId: session.trainer_user_id ?? '',
    capacity: String(session.capacity),
    agePreset: presetFor(
      session.min_age === null ? '' : String(session.min_age),
      session.max_age === null ? '' : String(session.max_age),
    ),
    minAge: session.min_age === null ? '' : String(session.min_age),
    maxAge: session.max_age === null ? '' : String(session.max_age),
    color: classColor(session.color),
    repeats: session.series !== null,
    weekdays: (session.series?.weekdays ?? [isoWeekday(sessionDay)]).map(String),
    date: sessionDay,
    startsOn: session.series?.starts_on ?? sessionDay,
    endsOn: session.series?.ends_on ?? '',
    startTime: timeOf(session.starts_at),
    endTime: timeOf(session.ends_at),
  }
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  /** The class being edited, or null for a new one. */
  session: ClassSession | null
  /** The day a new class starts on. */
  defaultDay: string
}

export function ClassFormModal({ isOpen, onClose, onSaved, session, defaultDay }: Props) {
  const { t } = useTranslation()
  const api = useCalendarApi()
  const [draft, setDraft] = useState<Draft>(() => draftFor(session, defaultDay))
  const [scope, setScope] = useState<Scope>('this')
  const [trainers, setTrainers] = useState<PersonCard[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setDraft(draftFor(session, defaultDay))
    setScope('this')
    setError(null)
    void api<{ trainers: PersonCard[] }>('/classes/trainers').then((res) => {
      if (res.ok) setTrainers(res.data.trainers)
    })
  }, [isOpen, session, defaultDay, api])

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const editing = session !== null
  const inSeries = session?.series_id != null
  // Which schedule the form shows: a weekly one (new repeating class, or every
  // upcoming class of a series), or one day.
  const weekly = editing ? inSeries && scope === 'upcoming' : draft.repeats

  function errorMessage(code: string | undefined, booked?: number): string {
    switch (code) {
      case 'CAPACITY_BELOW_BOOKED':
        return t('calendar.errorCapacityBelowBooked', { count: booked ?? 0 })
      case 'WEEKDAYS':
        return t('calendar.errorWeekdays')
      case 'TIMES':
        return t('calendar.errorTimes')
      case 'DATES':
        return t('calendar.errorDates')
      case 'AGE_RANGE':
        return t('calendar.errorAgeRange')
      case 'TITLE_REQUIRED':
        return t('calendar.errorTitle')
      case 'CLASH':
        return t('calendar.errorClash')
      case 'PAST':
        return t('calendar.errorPast')
      default:
        return t('calendar.errorSave')
    }
  }

  async function save() {
    setError(null)
    if (!draft.title.trim()) return setError(t('calendar.errorTitle'))
    if (!draft.startTime || !draft.endTime || draft.endTime <= draft.startTime)
      return setError(t('calendar.errorTimes'))
    if (weekly && !draft.weekdays.length) return setError(t('calendar.errorWeekdays'))
    if (draft.minAge && draft.maxAge && Number(draft.minAge) > Number(draft.maxAge))
      return setError(t('calendar.errorAgeRange'))

    const fields = {
      title: draft.title.trim(),
      description: draft.description.trim() || null,
      trainer_user_id: draft.trainerId || null,
      capacity: Number(draft.capacity),
      min_age: draft.minAge === '' ? null : Number(draft.minAge),
      max_age: draft.maxAge === '' ? null : Number(draft.maxAge),
      color: draft.color,
      start_time: draft.startTime,
      end_time: draft.endTime,
    }
    const schedule = {
      weekdays: draft.weekdays.map(Number),
      ends_on: draft.endsOn || null,
    }

    let res
    setSaving(true)
    try {
      if (!session) {
        res = draft.repeats
          ? await api('/class-series', 'POST', {
              ...fields,
              ...schedule,
              starts_on: draft.startsOn,
            })
          : await api('/class-sessions', 'POST', { ...fields, date: draft.date })
      } else if (weekly) {
        res = await api<{ kept: number }>(`/class-series/${session.series_id}`, 'PATCH', {
          ...fields,
          ...schedule,
          from: session.starts_at.slice(0, 10),
        })
      } else {
        res = await api(`/class-sessions/${session.id}`, 'PATCH', { ...fields, date: draft.date })
      }
    } catch {
      setSaving(false)
      return setError(t('calendar.errorSave'))
    }
    setSaving(false)

    if (!res.ok)
      return setError(errorMessage(res.data.code, (res.data as { booked?: number }).booked))
    const kept = (res.data as { kept?: number }).kept ?? 0
    if (kept > 0)
      addToast({ title: t('calendar.keptAtOldTime', { count: kept }), color: 'warning' })
    onSaved()
    onClose()
  }

  const weekdayKeys = [1, 2, 3, 4, 5, 6, 7]

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>{editing ? t('calendar.editClass') : t('calendar.newClass')}</ModalHeader>
        <ModalBody className="gap-4">
          {inSeries && (
            <RadioGroup
              label={t('calendar.scopeLabel')}
              orientation="horizontal"
              value={scope}
              onValueChange={(v) => setScope(v as Scope)}
            >
              <Radio value="this">{t('calendar.scopeThis')}</Radio>
              <Radio value="upcoming">{t('calendar.scopeUpcoming')}</Radio>
            </RadioGroup>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t('calendar.fieldTitle')}
              value={draft.title}
              onValueChange={(v) => set('title', v)}
              className="sm:col-span-2"
            />
            <Select
              label={t('calendar.fieldTrainer')}
              selectedKeys={draft.trainerId ? [draft.trainerId] : []}
              onSelectionChange={(keys) => set('trainerId', (Array.from(keys)[0] as string) ?? '')}
            >
              {trainers.map((tr) => {
                const name = trainerLabel(tr)
                return (
                  <SelectItem key={tr.id} textValue={name}>
                    {name}
                  </SelectItem>
                )
              })}
            </Select>
            <Input
              type="number"
              min={1}
              label={t('calendar.fieldCapacity')}
              description={t('calendar.fieldCapacityHint')}
              value={draft.capacity}
              onValueChange={(v) => set('capacity', v)}
            />
          </div>

          <RadioGroup
            label={t('calendar.fieldAgeGroup')}
            orientation="horizontal"
            value={draft.agePreset}
            onValueChange={(v) => {
              const picked = v as AgePreset
              // Custom keeps whatever ages are there, to be edited; the others fill them in.
              setDraft((d) =>
                picked === 'custom'
                  ? { ...d, agePreset: picked }
                  : {
                      ...d,
                      agePreset: picked,
                      minAge: AGE_PRESETS[picked].min,
                      maxAge: AGE_PRESETS[picked].max,
                    },
              )
            }}
          >
            <Radio value="all">{t('calendar.ageAll')}</Radio>
            <Radio value="kids" description={t('calendar.ageBand', { band: '5–17' })}>
              {t('calendar.ageKids')}
            </Radio>
            <Radio value="adults" description={t('calendar.ageBand', { band: '18+' })}>
              {t('calendar.ageAdults')}
            </Radio>
            <Radio value="custom">{t('calendar.ageCustom')}</Radio>
          </RadioGroup>
          {draft.agePreset === 'custom' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                type="number"
                min={0}
                label={t('calendar.fieldMinAge')}
                description={t('calendar.fieldAgeHint')}
                value={draft.minAge}
                onValueChange={(v) => set('minAge', v)}
              />
              <Input
                type="number"
                min={0}
                label={t('calendar.fieldMaxAge')}
                description={t('calendar.fieldAgeHint')}
                value={draft.maxAge}
                onValueChange={(v) => set('maxAge', v)}
              />
            </div>
          )}

          {!editing && (
            <Switch isSelected={draft.repeats} onValueChange={(v) => set('repeats', v)}>
              <p className="text-sm">{t('calendar.fieldRepeats')}</p>
            </Switch>
          )}

          {weekly ? (
            <>
              <CheckboxGroup
                label={t('calendar.fieldWeekdays')}
                orientation="horizontal"
                value={draft.weekdays}
                onValueChange={(v) => set('weekdays', v)}
              >
                {weekdayKeys.map((d) => (
                  <Checkbox key={d} value={String(d)}>
                    {t(`calendar.weekday${d}`)}
                  </Checkbox>
                ))}
              </CheckboxGroup>
              <div className="grid gap-3 sm:grid-cols-2">
                {!editing && (
                  <DateField
                    label={t('calendar.fieldStartsOn')}
                    value={draft.startsOn}
                    onValueChange={(v) => set('startsOn', v)}
                  />
                )}
                <DateField
                  label={t('calendar.fieldEndsOn')}
                  description={t('calendar.fieldEndsOnHint')}
                  value={draft.endsOn}
                  onValueChange={(v) => set('endsOn', v)}
                />
              </div>
            </>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <DateField
                label={t('calendar.fieldDate')}
                value={draft.date}
                onValueChange={(v) => set('date', v)}
              />
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <TimeField
              label={t('calendar.fieldStartTime')}
              value={draft.startTime}
              onValueChange={(v) => set('startTime', v)}
            />
            <TimeField
              label={t('calendar.fieldEndTime')}
              value={draft.endTime}
              onValueChange={(v) => set('endTime', v)}
            />
          </div>

          <Select
            label={t('calendar.fieldColor')}
            selectedKeys={[draft.color]}
            disallowEmptySelection
            onSelectionChange={(keys) => set('color', Array.from(keys)[0] as ClassColor)}
            startContent={
              <div className={`h-3 w-3 shrink-0 rounded-full ${COLOR_SWATCH[draft.color]}`} />
            }
          >
            {CLASS_COLORS.map((c) => (
              <SelectItem
                key={c}
                textValue={t(`calendar.color_${c}`)}
                startContent={<div className={`h-3 w-3 rounded-full ${COLOR_SWATCH[c]}`} />}
              >
                {t(`calendar.color_${c}`)}
              </SelectItem>
            ))}
          </Select>

          <Textarea
            label={t('calendar.fieldDescription')}
            value={draft.description}
            onValueChange={(v) => set('description', v)}
            minRows={2}
          />

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="primary" isLoading={saving} onPress={() => void save()}>
            {editing ? t('common.save') : t('calendar.create')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
